import usePageTitle from "@/lib/usePageTitle";
import { IMAGES } from "@/lib/content";
import { useContent } from "@/lib/content-context";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export default function Products() {
  usePageTitle("Products");
  const { content } = useContent();
  const p = content.products;

  return (
    <div data-testid="products-page">
      {/* HERO */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="overline mb-6">{p.hero_overline}</div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.98] tracking-tight">
              {p.hero_line1}<br/><span className="italic">{p.hero_line2}</span>
            </h1>
            <p className="mt-8 text-[#5C5A56] text-lg leading-relaxed max-w-2xl">{p.hero_paragraph}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] bg-cover bg-center" style={{ backgroundImage: `url('${IMAGES.products}')` }} />
          </div>
        </div>
      </section>

      {/* YARN FAMILIES */}
      <section className="py-20 md:py-28 bg-[#EAE6DF]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-5">{p.families_overline}</div>
          <h2 className="font-serif-display text-4xl md:text-5xl mb-14 max-w-3xl">{p.families_heading}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {(p.yarn_types || []).map((y, i) => (
              <article key={i} data-testid={`yarn-card-${i}`} className="editorial-card p-8 md:p-10 flex flex-col">
                <span className="overline text-[#C25934]">{y.tag}</span>
                <h3 className="font-serif-display text-2xl md:text-3xl mt-4 leading-tight">
                  {y.name}
                  {y.short && <span className="block text-[#5C5A56] text-base mt-1 font-sans font-mono-ed tracking-widest">{y.short}</span>}
                </h3>
                <p className="text-[#5C5A56] mt-4 text-sm leading-relaxed flex-1">{y.desc}</p>
                <div className="mt-6 pt-6 border-t border-[#1A1A1A]/10 flex items-center justify-between">
                  <span className="text-xs text-[#5C5A56] font-mono-ed">#{String(i + 1).padStart(2, "0")}</span>
                  <Link to="/contact" className="link-underline text-xs uppercase tracking-widest" data-testid={`yarn-card-cta-${i}`}>
                    Request sample
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* COUNT RANGE */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="overline mb-5">{p.count_overline}</div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
                From <span className="italic">6 Ne</span> to <span className="italic">50 Ne</span>.
              </h2>
              <p className="mt-6 text-[#5C5A56] leading-relaxed">{p.count_paragraph}</p>
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[#1A1A1A]/10 border border-[#1A1A1A]/10">
                {(p.count_grid || []).map((c, i) => (
                  <div key={i} data-testid={`count-cell-${i}`} className="bg-[#F8F7F4] p-6 md:p-8 hover:bg-[#EAE6DF] transition-colors">
                    <div className="font-mono-ed text-xs text-[#C25934]">{String(i + 1).padStart(2, "0")}</div>
                    <div className="font-serif-display text-2xl mt-3">{c.range}</div>
                    <div className="text-xs text-[#5C5A56] mt-2 tracking-wide uppercase">{c.note}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A1A1A] text-[#F8F7F4] py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h3 className="font-serif-display text-3xl md:text-5xl max-w-2xl">{p.cta_heading}</h3>
          <Link to="/contact" data-testid="products-cta" className="btn-primary bg-[#C25934] border-[#C25934] hover:bg-[#A84826]">
            {p.cta_button} <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
